import type { AuthWebappPermissionAtom } from "@/gen/orval/webplatform/model/authWebappPermissionAtom.gen";
import type { MetadataAgoraTags, MetadataPageName } from "@/lib/utils/metadata";

export type Feature = VirtualMachineFeature;
export type VirtualMachine = "ethereum" | "solana";
export type VirtualMachineFeature = "instant-liquidity";

export const DEFAULT_VIRTUAL_MACHINE: VirtualMachine = "ethereum";
const VALID_VIRTUAL_MACHINES: VirtualMachine[] = ["ethereum", "solana"];
const VALID_FEATURES: Feature[] = ["instant-liquidity"];

export type EthereumVirtualMachineChainId = number;
export const DEFAULT_ETHEREUM_VIRTUAL_MACHINE_CHAIN_ID: EthereumVirtualMachineChainId = 1;

export type SolanaVirtualMachineClusterName = "devnet" | "mainnet-beta" | "testnet";
export const DEFAULT_SOLANA_VIRTUAL_MACHINE_CLUSTER_NAME: SolanaVirtualMachineClusterName = "mainnet-beta";

export const VALID_FEATURES_BY_VIRTUAL_MACHINE: Record<VirtualMachine, Feature[]> = {
  ethereum: ["instant-liquidity"],
  solana: [],
};

export function getSupportedVMsForFeature(feature: Feature): VirtualMachine[] {
  return VALID_VIRTUAL_MACHINES.filter((vm) => VALID_FEATURES_BY_VIRTUAL_MACHINE[vm].includes(feature));
}

export function checkIfFeatureIsSupportedByCurrentVm(vm: VirtualMachine, feature: Feature): boolean {
  return VALID_FEATURES_BY_VIRTUAL_MACHINE[vm]?.includes(feature) ?? false;
}

export function checkIfFeatureExists(feature: null | string): feature is Feature {
  return feature !== null && VALID_FEATURES.includes(feature as Feature);
}

export const METADATA_BY_FEATURE: Record<
  Feature,
  {
    description: string;
    prettyName: string;
    title: string;
    pageName: MetadataPageName;
    pageTags?: MetadataAgoraTags[];
  }
> = {
  "instant-liquidity": {
    description: "Turn fiat or tokens into AUSD in just a few steps",
    prettyName: "Instant Liquidity",
    title: "Instant Liquidity",
    pageName: "webapp.customer.swap-widget",
    pageTags: ["customer", "exchange", "instant-liquidity", "swap"],
  },
};

// RBAC atoms required to view each feature, used by the [feature] route gate.
// Any user holding at least one atom in the list may view the feature; the
// page itself applies finer-grained per-control gating where needed.
export const PERMISSIONS_BY_FEATURE: Record<
  Feature,
  readonly [AuthWebappPermissionAtom, ...AuthWebappPermissionAtom[]]
> = {
  "instant-liquidity": ["mint:write", "redeem:write"],
};

export const METADATA_BY_VIRTUAL_MACHINE: Record<VirtualMachine, { prettyName: string; shortName: string }> = {
  ethereum: {
    prettyName: "Ethereum",
    shortName: "evm",
  },
  solana: {
    prettyName: "Solana",
    shortName: "svm",
  },
};

export function createVirtualMachineString(vms: VirtualMachine[]) {
  const formatted = vms.map((vm) => {
    const metadata = METADATA_BY_VIRTUAL_MACHINE[vm as VirtualMachine];
    return `${metadata.prettyName} (${metadata.shortName.toUpperCase()})`;
  });

  if (formatted.length <= 1) return formatted[0] || "";
  if (formatted.length === 2) return formatted.join(" and ");

  return formatted.slice(0, -1).join(", ") + " and " + formatted[formatted.length - 1];
}
